import React, { useMemo } from 'react';
import moment from 'moment';
import { useShowContext } from 'framework/show';
import { DATE_FORMAT } from 'framework/utils';
import BalancePieChart from '../shared/BalancePieChart';
import { ShowPortalLeaveType } from '../Show/type';

const SickDaysChart: React.FC = () => {
  const { data } = useShowContext<ShowPortalLeaveType>();
  const { sickBalances, currentDate, hasSickDaysBenefit } = data.getData() || {};

  const COLORS = {
    sickUsed: 'var(--mui-palette-background-default)',
    sickAvailable: 'var(--mui-palette-secondary-main)',
  };

  const currentPeriod = useMemo(() => {
    return sickBalances.find((period: { startDate: string; endDate: string }) => {
      const start = moment(period.startDate);
      const end = moment(period.endDate);
      const current = moment(currentDate);

      return current.isSameOrAfter(start, 'day') && current.isSameOrBefore(end, 'day');
    });
  }, [currentDate, sickBalances]);

  const nextPeriod = useMemo(() => {
    return sickBalances
      .filter((period: { startDate: string }) => moment(period.startDate).isAfter(moment(currentDate), 'day'))
      .sort(
        (a: { startDate: string }, b: { startDate: string }) =>
          moment(a.startDate).valueOf() - moment(b.startDate).valueOf(),
      )[0];
  }, [currentDate, sickBalances]);

  const chartData = useMemo(() => {
    const totalBalance = Number(currentPeriod?.sickDaysTotalBalance || 0);
    const remainedBalance = Number(currentPeriod?.sickDaysRemainedBalance || 0);

    return [
      { name: 'Used', value: totalBalance - remainedBalance, color: COLORS.sickUsed },
      { name: 'Available', value: remainedBalance, color: COLORS.sickAvailable },
    ];
  }, [COLORS.sickAvailable, COLORS.sickUsed, currentPeriod]);
  const remainedBalance = useMemo(() => Number(currentPeriod?.sickDaysRemainedBalance || 0), [currentPeriod]);
  if (!hasSickDaysBenefit || !sickBalances?.length) return null;

  return (
    <BalancePieChart
      title="Sick Days Balance"
      chartData={chartData}
      totalAvailable={remainedBalance}
      nextPeriodText={
        nextPeriod && Number(nextPeriod.sickDaysTotalBalance) > 0 ? (
          <>
            <strong>
              {nextPeriod.sickDaysTotalBalance} {Number(nextPeriod.sickDaysTotalBalance) === 1 ? 'day' : 'days'}
            </strong>{' '}
            of sick day balance will become available on{' '}
            <strong>{moment(nextPeriod.startDate).format(DATE_FORMAT.NATURAL_SHORT)}</strong>
          </>
        ) : undefined
      }
    />
  );
};

export default SickDaysChart;
